import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  FlatList,
  TextInput,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from 'react-native';

import { X, Search, Check, AlertCircle, Lock, Link2 } from './../icons';
import { teamMembersService } from '../../services/teamMembersService';
import { spacing } from '../../theme/spacing';
import { TeamMember } from '../../types';
import { getVertivEmailVariants } from '../../utils/emailNormalization';
import { KeyboardDismissToolbar } from '../shared/KeyboardDismissToolbar';

const TEAM_MEMBER_ACCESSORY_VIEW_ID = 'team-member-keyboard-dismiss';

interface TeamMemberSelectorProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (selectedEmails: string[]) => void;
  selectedIds: string[]; // Keep for backward compatibility, now contains emails
  type: 'vertiv_sponsor' | 'vertiv_contributor' | 'partner_contributor';
  title: string;
  /** Email that was auto-added from deal - shown with link indicator but CAN be deselected */
  linkedEmail?: string;
  // Default Sponsor mode props
  currentRegion?: string;
  allowDefaultSponsorMode?: boolean;
  onDefaultSponsorToggle?: (useDefault: boolean) => void;
  useDefaultSponsors?: boolean;
}

export const TeamMemberSelector: React.FC<TeamMemberSelectorProps> = ({
  visible,
  onClose,
  onSelect,
  selectedIds = [],
  type,
  title,
  linkedEmail,
  currentRegion,
  allowDefaultSponsorMode = false,
  onDefaultSponsorToggle,
  useDefaultSponsors = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<TeamMember[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>(selectedIds);
  const [loading, setLoading] = useState(false);

  // Default Sponsor mode state
  const [showDefaultSponsorMode, setShowDefaultSponsorMode] =
    useState(useDefaultSponsors);
  const [defaultSponsors, setDefaultSponsors] = useState<TeamMember[]>([]);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [defaultSponsorsError, setDefaultSponsorsError] = useState<
    string | null
  >(null);
  const [retryCount, setRetryCount] = useState(0);
  const savedSearchQuery = useRef<string>('');

  // Track initial state to handle Cancel properly
  const initialStateRef = useRef({
    useDefaultSponsors: useDefaultSponsors,
    showDefaultSponsorMode: useDefaultSponsors,
  });

  // Load team members when modal opens
  useEffect(() => {
    const loadTeamMembers = async () => {
      setLoading(true);
      try {
        // Use getUsersByRole which fetches from role-management API
        const data = await teamMembersService.getUsersByRole(type);
        // Sort alphabetically by name (fallback to email if no name)
        const sorted = [...data].sort((a, b) => {
          const nameA = (a.name || a.email || '').toLowerCase();
          const nameB = (b.name || b.email || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setMembers(sorted);
        setFilteredMembers(sorted);
      } catch (error) {
        console.error('Failed to load team members:', error);
      } finally {
        setLoading(false);
      }
    };

    if (visible) {
      loadTeamMembers();
    }
  }, [visible, type]);

  // Track previous selectedIds to prevent unnecessary updates
  const prevSelectedIdsRef = useRef<string>(JSON.stringify(selectedIds));

  // Sync state with props when modal opens - combined to prevent race conditions
  useEffect(() => {
    if (visible) {
      // First, sync Default Sponsor mode
      setShowDefaultSponsorMode(useDefaultSponsors);

      // Store initial state for Cancel functionality
      initialStateRef.current = {
        useDefaultSponsors: useDefaultSponsors,
        showDefaultSponsorMode: useDefaultSponsors,
      };

      // Then, sync selections only if NOT in Default Sponsor mode
      // (Default Sponsor mode will load and auto-select via separate useEffect)
      if (!useDefaultSponsors) {
        const uniqueSelectedIds = Array.from(new Set(selectedIds));
        const currentIdsString = JSON.stringify(uniqueSelectedIds.sort());

        // Only update if selectedIds actually changed
        if (prevSelectedIdsRef.current !== currentIdsString) {
          setSelectedItems(uniqueSelectedIds);
          prevSelectedIdsRef.current = currentIdsString;
        }
      }
    }
  }, [visible, selectedIds, useDefaultSponsors, type]);

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setShowDefaultSponsorMode(false);
      setDefaultSponsors([]);
      setLoadingDefaults(false);
      setDefaultSponsorsError(null);
      setSearchQuery('');
      savedSearchQuery.current = ''; // F4: Reset saved search query
    }
  }, [visible]);

  // Load Default Sponsors when Default Sponsor mode is active and region is set
  useEffect(() => {
    let isCancelled = false;

    const loadDefaultSponsors = async () => {
      if (
        !showDefaultSponsorMode ||
        !currentRegion ||
        type !== 'vertiv_sponsor'
      ) {
        return;
      }

      if (!isCancelled) {
        setLoadingDefaults(true);
        setDefaultSponsorsError(null);
      }

      try {
        const sponsors = await teamMembersService.getDefaultSponsorsByRegion(
          currentRegion,
        );

        // Check if component is still mounted, request not cancelled, AND still in Default Sponsor mode
        if (!isCancelled && showDefaultSponsorMode) {
          setDefaultSponsors(sponsors);
          setDefaultSponsorsError(null);
          // Don't update selectedItems here - wait for user to click "Done"
          // Only store the loaded sponsors in defaultSponsors state
        }
      } catch (error) {
        // Check if component is still mounted before updating state
        if (!isCancelled) {
          console.error('Failed to load Default Sponsors:', error);
          setDefaultSponsors([]);
          setDefaultSponsorsError(
            'Failed to load Default Sponsors. Please try again.',
          );
        }
      } finally {
        // Check if component is still mounted before updating state
        if (!isCancelled) {
          setLoadingDefaults(false);
        }
      }
    };

    loadDefaultSponsors();

    // Cleanup function to cancel in-flight requests
    return () => {
      isCancelled = true;
    };
  }, [showDefaultSponsorMode, currentRegion, type, retryCount]);

  // Helper for case-insensitive email comparison
  const isEmailInList = useCallback(
    (email: string, emailList: string[]): boolean => {
      const normalizedEmail = email?.toLowerCase();
      return emailList.some(e => e?.toLowerCase() === normalizedEmail);
    },
    [],
  );

  // Sort with selected items at top, then alphabetically within each group
  const sortWithSelectedFirst = useCallback(
    (items: TeamMember[], selectedEmails: string[]) => {
      return [...items].sort((a, b) => {
        const aSelected = isEmailInList(a.email, selectedEmails);
        const bSelected = isEmailInList(b.email, selectedEmails);

        // If selection status differs, selected items come first
        if (aSelected !== bSelected) {
          return aSelected ? -1 : 1;
        }

        // Within the same selection status, sort alphabetically
        const nameA = (a.name || a.email || '').toLowerCase();
        const nameB = (b.name || b.email || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    },
    [isEmailInList],
  );

  // Filter and sort members when search query or selection changes
  useEffect(() => {
    const filterMembers = () => {
      let filtered = members;

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = members.filter(
          member =>
            (member.email || '').toLowerCase().includes(query) ||
            (member.firstName || '').toLowerCase().includes(query) ||
            (member.lastName || '').toLowerCase().includes(query) ||
            (member.name || '').toLowerCase().includes(query),
        );
      }

      // Sort with selected items at top, then alphabetically
      const sorted = sortWithSelectedFirst(filtered, selectedItems);
      setFilteredMembers(sorted);
    };

    filterMembers();
  }, [searchQuery, members, selectedItems, sortWithSelectedFirst]);

  const toggleSelection = (email: string) => {
    // F8: Type guard - don't allow empty emails
    if (!email || email.trim() === '') {
      console.error('Attempted to select team member with empty email');
      return;
    }

    setSelectedItems(prev => {
      // Use case-insensitive check for existing selection
      const normalizedEmail = email.toLowerCase();
      const existingIndex = prev.findIndex(
        item => item?.toLowerCase() === normalizedEmail,
      );

      if (existingIndex >= 0) {
        return prev.filter((_, index) => index !== existingIndex);
      }
      return [...prev, email];
    });
  };

  const handleCancel = () => {
    // Revert to initial state if user changed their mind
    if (
      showDefaultSponsorMode !== initialStateRef.current.showDefaultSponsorMode
    ) {
      setShowDefaultSponsorMode(initialStateRef.current.showDefaultSponsorMode);
      setDefaultSponsors([]);
      setDefaultSponsorsError(null);
    }
    onClose();
  };

  const handleDone = () => {
    // When in Default Sponsor mode, use the default sponsors
    // This ensures default sponsors are only added when "Done" is clicked
    if (showDefaultSponsorMode) {
      // Always notify parent when in default sponsor mode, even if no sponsors loaded
      // This ensures useDefaultSponsors flag is set correctly in parent
      if (onDefaultSponsorToggle) {
        try {
          onDefaultSponsorToggle(true);
        } catch (error) {
          console.error('Error calling onDefaultSponsorToggle:', error);
        }
      }

      if (defaultSponsors.length > 0) {
        const sponsorEmails = defaultSponsors.map(s => s.email).filter(Boolean);
        onSelect(sponsorEmails);
      } else {
        // No default sponsors available, but still in default sponsor mode
        onSelect([]);
      }
    } else {
      // Not in default sponsor mode
      if (useDefaultSponsors) {
        // User unchecked the box - notify parent
        onDefaultSponsorToggle?.(false);
      }
      onSelect(selectedItems);
    }
    onClose();
  };

  const renderMemberItem = ({ item }: { item: TeamMember }) => {
    const isSelected = isEmailInList(item.email, selectedItems);
    const hasName = item.name && item.name !== item.email;
    const isLinked =
      linkedEmail && item.email.toLowerCase() === linkedEmail.toLowerCase();

    return (
      <TouchableOpacity
        style={[styles.memberItem, isSelected && styles.selectedMemberItem]}
        onPress={() => toggleSelection(item.email)}
        activeOpacity={0.7}
      >
        <View style={styles.memberInfo}>
          <View style={styles.memberNameRow}>
            {hasName && (
              <Text
                style={[
                  styles.memberName,
                  isSelected && styles.selectedMemberName,
                ]}
              >
                {item.name}
              </Text>
            )}
            {isLinked && (
              <View
                style={styles.linkedBadge}
                testID="linked-deal-indicator"
                accessibilityLabel="Auto-added from linked deal"
              >
                <Link2 size={12} color="#ff6b35" />
              </View>
            )}
          </View>
          <Text
            style={[
              styles.memberEmail,
              isSelected && styles.selectedMemberEmail,
            ]}
          >
            {(item.email || '').toLowerCase()}
          </Text>
        </View>
        <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
          {isSelected && <Check size={16} color="#fff" />}
        </View>
      </TouchableOpacity>
    );
  };

  // Render Default Sponsor items (read-only, always checked)
  const renderDefaultSponsorItem = ({ item }: { item: TeamMember }) => {
    const hasName = item.name && item.name !== item.email;

    return (
      <View
        style={[
          styles.memberItem,
          styles.defaultSponsorItem,
          styles.lockedItem,
        ]}
      >
        <View style={styles.memberInfo}>
          {hasName && (
            <Text style={[styles.memberName, styles.dimmedText]}>
              {item.name}
            </Text>
          )}
          <Text style={[styles.memberEmail, styles.dimmedText]}>
            {(item.email || '').toLowerCase()}
          </Text>
          <View style={styles.badgeRow}>
            <View style={styles.defaultBadgeContainer}>
              <Text style={styles.defaultBadge}>Default Sponsor</Text>
            </View>
          </View>
        </View>
        <View style={styles.checkboxLocked}>
          <Lock size={14} color="#666" />
        </View>
      </View>
    );
  };

  const renderHeader = () => (
    <>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>
          {title}{' '}
          {selectedItems.length > 0 && `(${selectedItems.length} selected)`}
        </Text>
        <TouchableOpacity
          style={styles.closeButton}
          onPress={handleCancel}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <X size={24} color="#333" />
        </TouchableOpacity>
      </View>

      {/* Info bar when Default Sponsors are loaded */}
      {showDefaultSponsorMode && defaultSponsors.length > 0 && (
        <View style={styles.infoBar} testID="default-sponsor-info-bar">
          <Text style={styles.infoBarText}>Default Sponsors Assigned</Text>
        </View>
      )}

      {/* Hide search bar when in Default Sponsor mode */}
      {!showDefaultSponsorMode && (
        <View style={styles.searchContainer}>
          <Search size={20} color="#999" style={styles.searchIcon} />
          <TextInput
            testID="team-member-search"
            style={styles.searchInput}
            placeholder="Search by name or email"
            value={searchQuery}
            onChangeText={setSearchQuery}
            autoCorrect={false}
            autoCapitalize="none"
            returnKeyType="search"
            inputAccessoryViewID={
              Platform.OS === 'ios' ? TEAM_MEMBER_ACCESSORY_VIEW_ID : undefined
            }
          />
        </View>
      )}
    </>
  );

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={styles.overlay}>
        <KeyboardDismissToolbar nativeID={TEAM_MEMBER_ACCESSORY_VIEW_ID} />
        <View style={styles.modal} testID="team-member-modal">
          {renderHeader()}

          <View style={styles.listContainer}>
            {showDefaultSponsorMode ? (
              // Default Sponsor Mode
              <>
                {!currentRegion ? (
                  // Validation: No region selected
                  <View
                    style={styles.messageContainer}
                    testID="default-sponsor-no-region"
                  >
                    <AlertCircle size={48} color="#ff6b35" />
                    <Text style={styles.warningText}>
                      Please select a region first to use Default Sponsors
                    </Text>
                  </View>
                ) : loadingDefaults ? (
                  // Loading state
                  <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color="#ff6b35" />
                    <Text style={styles.emptyText}>
                      Loading Default Sponsors...
                    </Text>
                  </View>
                ) : defaultSponsorsError ? (
                  // F4: Error state - API call failed
                  <View style={styles.messageContainer}>
                    <AlertCircle size={48} color="#dc3545" />
                    <Text style={styles.errorText}>{defaultSponsorsError}</Text>
                    <TouchableOpacity
                      style={styles.retryButton}
                      onPress={() => {
                        // Retry by incrementing counter to trigger useEffect
                        setDefaultSponsorsError(null);
                        setRetryCount(prev => prev + 1);
                      }}
                    >
                      <Text style={styles.retryButtonText}>Retry</Text>
                    </TouchableOpacity>
                  </View>
                ) : defaultSponsors.length === 0 ? (
                  // No Default Sponsors configured
                  <View style={styles.messageContainer}>
                    <AlertCircle size={48} color="#ffa500" />
                    <Text style={styles.infoText}>
                      No Default Sponsors configured for{' '}
                      {currentRegion.toUpperCase()} region.
                    </Text>
                  </View>
                ) : (
                  // Show Default Sponsors (non-interactive)
                  <FlatList
                    testID="default-sponsor-list"
                    data={defaultSponsors}
                    renderItem={renderDefaultSponsorItem}
                    keyExtractor={item => `default-${item.email}`}
                    contentContainerStyle={styles.listContent}
                    showsVerticalScrollIndicator={true}
                  />
                )}
              </>
            ) : (
              // Normal manual selection mode
              <>
                {loading ? (
                  <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color="#ff6b35" />
                    <Text style={styles.emptyText}>
                      Loading team members...
                    </Text>
                  </View>
                ) : filteredMembers.length === 0 ? (
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>
                      {searchQuery
                        ? 'No matching team members found'
                        : 'No team members available'}
                    </Text>
                  </View>
                ) : (
                  <FlatList
                    data={filteredMembers}
                    keyExtractor={(item, index) =>
                      `${type}-${item.email}-${index}`
                    }
                    renderItem={renderMemberItem}
                    contentContainerStyle={styles.listContent}
                    showsVerticalScrollIndicator={true}
                    nestedScrollEnabled={true}
                    removeClippedSubviews={true}
                    maxToRenderPerBatch={10}
                    windowSize={10}
                  />
                )}
              </>
            )}
          </View>

          <View style={styles.footer}>
            {/* Default Sponsor checkbox (left side) */}
            {/* Show when allowed by parent, OR when linked sponsor has been deselected in this session */}
            {type === 'vertiv_sponsor' &&
              (allowDefaultSponsorMode ||
                (linkedEmail &&
                  !getVertivEmailVariants(linkedEmail).some(v =>
                    isEmailInList(v, selectedItems),
                  ))) && (
                <TouchableOpacity
                  testID="default-sponsor-checkbox"
                  style={styles.defaultSponsorCheckboxContainer}
                  onPress={() => {
                    const newValue = !showDefaultSponsorMode;
                    setShowDefaultSponsorMode(newValue);

                    // Reset state when toggling modes
                    if (newValue) {
                      // Entering Default Sponsor mode
                      savedSearchQuery.current = searchQuery;
                      setSearchQuery('');
                      // BUG FIX: Clear selected items immediately to prevent premature updates
                      setSelectedItems([]);
                    } else {
                      // Exiting Default Sponsor mode - clear everything
                      setDefaultSponsors([]);
                      setDefaultSponsorsError(null);
                      setSearchQuery(savedSearchQuery.current);
                      // Clear selections - parent will provide fresh selectedIds on next render
                      setSelectedItems([]);
                    }

                    // BUG FIX: Don't notify parent here - wait until "Done" is clicked
                    // onDefaultSponsorToggle will be called in handleDone()
                  }}
                  activeOpacity={0.7}
                >
                  <View
                    style={[
                      styles.checkbox,
                      showDefaultSponsorMode && styles.checkboxSelected,
                    ]}
                  >
                    {showDefaultSponsorMode && <Check size={16} color="#fff" />}
                  </View>
                  <Text style={styles.defaultSponsorCheckboxLabel}>
                    I don't know my Sponsor
                  </Text>
                </TouchableOpacity>
              )}

            {/* Action buttons (right side) */}
            <View style={styles.footerButtons}>
              <TouchableOpacity
                testID="team-member-cancel-btn"
                style={styles.cancelButton}
                onPress={handleCancel}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                testID="team-member-done-btn"
                style={[
                  styles.doneButton,
                  loadingDefaults ||
                  (showDefaultSponsorMode && defaultSponsorsError)
                    ? styles.doneButtonDisabled
                    : null,
                ]}
                onPress={handleDone}
                disabled={
                  loadingDefaults ||
                  (showDefaultSponsorMode && defaultSponsorsError !== null)
                }
              >
                <Text style={styles.doneButtonText}>
                  Done {selectedItems.length > 0 && `(${selectedItems.length})`}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '100%',
    maxWidth: 600,
    ...Platform.select({
      web: {
        maxHeight: '90vh' as any,
        display: 'flex' as any,
        flexDirection: 'column' as any,
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)' as any,
      },
      default: {
        height: '80%',
      },
    }),
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    backgroundColor: '#fff',
    ...Platform.select({
      web: {
        flexShrink: 0,
        position: 'sticky' as any,
        top: 0,
        zIndex: 1,
      },
    }),
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  closeButton: {
    padding: 4,
  },
  infoBar: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: '#f8f9fa',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    ...Platform.select({
      web: {
        flexShrink: 0,
        position: 'sticky' as any,
        top: 57, // Height of header
        zIndex: 1,
      },
    }),
  },
  infoBarText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 14,
    backgroundColor: '#f8f9fa',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    ...Platform.select({
      web: {
        flexShrink: 0,
        position: 'sticky' as any,
        top: 57, // Height of header
        zIndex: 1,
      },
    }),
  },
  searchIcon: {
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#333',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    ...Platform.select({
      web: {
        outlineStyle: 'none' as const,
        outlineWidth: 0,
      },
    }),
  },
  listContainer: {
    flex: 1,
    ...Platform.select({
      web: {
        minHeight: 200,
        maxHeight: 'calc(90vh - 250px)' as any,
        overflow: 'auto' as any,
      },
      default: {
        // On mobile, let flex: 1 handle the height naturally
      },
    }),
  },
  listContent: {
    paddingVertical: 8,
    paddingBottom: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
  memberItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        ':hover': {
          backgroundColor: '#f8f9fa',
        },
      },
    }),
  },
  selectedMemberItem: {
    backgroundColor: '#f0f7ff',
  },
  memberInfo: {
    flex: 1,
    marginRight: 12,
  },
  memberNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  memberName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    marginBottom: 2,
  },
  selectedMemberName: {
    color: '#007bff',
  },
  linkedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  memberEmail: {
    fontSize: 14,
    color: '#666',
  },
  selectedMemberEmail: {
    color: '#0056b3',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: '#ddd',
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: '#007bff',
    borderColor: '#007bff',
  },
  footer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.modalInnerPadding,
    paddingVertical: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    backgroundColor: '#fff',
    ...Platform.select({
      web: {
        flexShrink: 0,
        position: 'sticky' as any,
        bottom: 0,
        columnGap: spacing.pdGridGutter,
        rowGap: spacing.md,
      },
      default: {
        gap: spacing.md,
      },
    }),
  },
  defaultSponsorCheckboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    minHeight: spacing.formRadioOptionMinHeight,
    ...Platform.select({
      web: {
        flex: 1,
        flexShrink: 1,
        minWidth: 200,
        maxWidth: '100%' as any,
        outlineOffset: spacing.xs,
        cursor: 'pointer' as any,
      },
      default: {
        width: '100%',
      },
    }),
  },
  defaultSponsorCheckboxLabel: {
    flex: 1,
    flexShrink: 1,
    fontSize: 14,
    color: '#333',
    marginLeft: spacing.formCheckboxLabelGap,
  },
  footerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    ...Platform.select({
      web: {
        flexShrink: 0,
      },
      default: {
        alignSelf: 'flex-end',
      },
    }),
  },
  cancelButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#fff',
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#666',
    fontWeight: '500',
  },
  doneButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 6,
    backgroundColor: '#ff6b35',
  },
  doneButtonDisabled: {
    backgroundColor: '#ccc',
    opacity: 0.6,
  },
  doneButtonText: {
    fontSize: 16,
    color: '#fff',
    fontWeight: '600',
  },
  // Default Sponsor mode styles
  messageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
    paddingVertical: 60,
  },
  warningText: {
    fontSize: 16,
    color: '#ff6b35',
    textAlign: 'center',
    marginTop: 16,
    fontWeight: '500',
  },
  infoText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginTop: 16,
    fontWeight: '500',
  },
  infoSubtext: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
    marginTop: 8,
  },
  errorText: {
    fontSize: 16,
    color: '#dc3545',
    textAlign: 'center',
    marginTop: 16,
    fontWeight: '500',
  },
  retryButton: {
    marginTop: 20,
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 6,
    backgroundColor: '#007bff',
  },
  retryButtonText: {
    fontSize: 16,
    color: '#fff',
    fontWeight: '600',
  },
  defaultSponsorItem: {
    backgroundColor: '#f0f7ff',
    opacity: 1,
  },
  // Locked item styles (for Default Sponsors)
  lockedItem: {
    opacity: 0.65,
    backgroundColor: '#f8f8f8',
    borderLeftWidth: 3,
    borderLeftColor: '#ccc',
  },
  dimmedText: {
    color: '#666',
  },
  badgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  defaultBadgeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#e0e0e0',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
  },
  defaultBadge: {
    fontSize: 12,
    color: '#666',
    fontWeight: '500',
  },
  checkboxLocked: {
    width: 24,
    height: 24,
    borderRadius: 4,
    backgroundColor: '#e8e8e8',
    borderWidth: 1,
    borderColor: '#aaa',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
